/**
 * Settings › Plugins › Connections › Browsers: a browser row's status line with its
 * dot, the line of help under it, and the chips at a Chromium row's end -
 * one per connection (the Denker extension, Apple Events), each an icon and
 * a dot.
 */
import type { ReactNode } from "react";
import { cva } from "class-variance-authority";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
  Spinner,
} from "@/components/ui";
import { useT } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import { cn } from "@/lib/cn";

/** Where one connection stands, and so what its chip does on a click. */
export type ConnectionPhase =
  | "connected"
  | "not_connected"
  | "needs_you"
  | "working"
  | "unavailable";

export type ConnectionTone = "connected" | "idle" | "attention";

const dotVariants = cva("inline-block h-1.5 w-1.5 shrink-0 rounded-full", {
  variants: {
    tone: {
      connected: "bg-success",
      idle: "bg-muted",
      attention: "bg-warning",
    },
  },
});

const PHASE_TONE: Record<
  Exclude<ConnectionPhase, "working">,
  ConnectionTone
> = {
  connected: "connected",
  not_connected: "idle",
  needs_you: "attention",
  unavailable: "idle",
};

function ConnectionDot({
  tone,
  className,
}: {
  tone: ConnectionTone;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      data-tone={tone}
      className={cn(dotVariants({ tone }), className)}
    />
  );
}

/** A row's one status line: the dot, then its words; `tooltip` (else the
 * words, which may be cut off) on hover. */
export function ConnectionStatus({
  testId,
  tone,
  label,
  tooltip,
}: {
  testId: string;
  tone: ConnectionTone;
  label: string;
  tooltip?: string | null;
}) {
  return (
    <span data-testid={testId} data-tone={tone} title={tooltip ?? label}>
      <ConnectionDot tone={tone} className="mr-1.5 align-middle" />
      {label}
    </span>
  );
}

/** An error or help under a row: one small muted line. */
export function ConnectionMessage({
  message,
}: {
  message: { text: string; alert: boolean };
}) {
  return (
    <p
      role={message.alert ? "alert" : "status"}
      className="text-appkit-small text-muted"
    >
      {message.text}
    </p>
  );
}

type ChipProps = {
  testId: string;
  icon: ReactNode;
  /** The connection, as its tooltip and menu name it: "Extension". */
  name: string;
  phase: ConnectionPhase;
  /** What a click does - or, unavailable, why not - after the name in the
   * tooltip: "Extension: connect". */
  does: string;
  /** Connected: the state its menu's header names. */
  state?: string;
  /** Not connected, needs you, or working: what a click runs. */
  onActivate?: () => void;
  onReconnect?: () => void;
  onDisconnect?: () => void;
};

/**
 * One connection's chip. Connected opens a menu with Reconnect and
 * Disconnect; not connected and needs you run `onActivate` (Connect, Turn on,
 * Guide me); working shows a spinner and runs `onActivate` (Stop) where it
 * can be stopped, else waits. Unavailable is dimmed and does nothing, its
 * tooltip saying why - it stays focusable so the tooltip can be read.
 */
export function ConnectionChip({
  testId,
  icon,
  name,
  phase,
  does,
  state,
  onActivate,
  onReconnect,
  onDisconnect,
}: ChipProps) {
  const t = useT(settingsBrowserMessages);
  const tooltip = t("chipTooltip", { name, does });
  const unavailable = phase === "unavailable";
  const button = {
    type: "button" as const,
    variant: "ghost" as const,
    size: "chip" as const,
    "data-testid": testId,
    "data-phase": phase,
    "aria-label": tooltip,
    title: tooltip,
    className: cn(
      unavailable &&
        "cursor-default opacity-40 hover:bg-transparent hover:text-muted",
    ),
  };
  const content = (
    <>
      <span className="flex [&>svg]:h-3 [&>svg]:w-3" aria-hidden="true">
        {icon}
      </span>
      {phase === "working" ? (
        <Spinner className="h-2.5 w-2.5 text-muted" />
      ) : (
        <ConnectionDot tone={PHASE_TONE[phase]} />
      )}
    </>
  );
  if (phase !== "connected")
    return (
      <Button
        {...button}
        aria-disabled={unavailable || undefined}
        aria-busy={phase === "working" || undefined}
        onClick={unavailable ? undefined : onActivate}
      >
        {content}
      </Button>
    );
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button {...button}>{content}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel>
          <span className="text-secondary">{name}</span>
          <span>{state}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<Icons.RefreshCw />}
          onSelect={onReconnect}
          data-testid={`${testId}-reconnect`}
        >
          {t("reconnect")}
        </DropdownMenuItem>
        <DropdownMenuItem
          icon={<Icons.Unplug />}
          onSelect={onDisconnect}
          data-testid={`${testId}-disconnect`}
        >
          {t("disconnect")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
